import { Trans, useTranslation } from 'react-i18next';

import {
	DOCUMENTS_TRIAL_LIMIT,
	DOCUMENT_TTL_HOURS,
	TRIAL_DAYS
} from '@/config';
import { useTrialDiscount } from '@/hooks/user';
import CircleCheck from '@/icons/check-circle.svg';
import { cn } from '@/utils';
import { formatPrice } from '@/utils/money';

function PlansFeatures({ plansData, className }) {
	const { t, i18n } = useTranslation();

	const trialDiscount = useTrialDiscount();

	const { plans, monthlyPlan, trialPlan } = plansData;

	const features = t('billing.features.table.items', { returnObjects: true });

	const featureValues = t('billing.features.table.values', {
		returnObjects: true
	});

	return (
		<div
			className={cn(
				'relative mt-[4.25rem] flex w-full flex-col pb-20',
				className
			)}
		>
			<h2 className="pb-12 font-title text-4xl font-semibold leading-[52px]">
				{t('billing.features.title')}
			</h2>

			<table className="border border-grey-200 bg-white p-4 font-semibold leading-7">
				<thead>
					<tr>
						<th className="border border-grey-200 p-4 text-left font-semibold leading-7">
							{t('billing.features.table.title')}
						</th>
						{plans.map(plan => {
							return (
								<td
									className="border border-grey-200 p-4 text-center font-semibold"
									key={plan.name}
								>
									{i18n.exists(`plans.${plan.name}.featureName`)
										? t(`plans.${plan.name}.featureName`)
										: t(`plans.${plan.name}.name`)}
								</td>
							);
						})}
					</tr>
				</thead>
				<tbody>
					{features.map((feat, index) => (
						<tr className="border border-grey-200" key={feat}>
							<th className="border border-grey-200 p-4 text-left font-semibold leading-7 text-grey-500">
								{feat}
							</th>
							{plans.map(plan => {
								const valueText = featureValues[plan.name]?.[index];

								const ariaLabel = valueText
									? undefined
									: t('common.yes');

								const limitedValues = valueText ? (
									<Trans
										defaults={valueText}
										components={{
											strong: <strong className="font-semibold" />
										}}
										values={{
											trialPrice: formatPrice({
												amount: trialDiscount
													? 0
													: trialPlan?.baseAmount,
												currency: trialPlan?.currency
											}),
											monthlyPrice: formatPrice({
												amount: monthlyPlan?.baseAmount,
												currency: monthlyPlan?.currency
											}),
											trialDays: TRIAL_DAYS,
											dayLimit: DOCUMENTS_TRIAL_LIMIT,
											documentTtlHours: DOCUMENT_TTL_HOURS
										}}
									/>
								) : (
									<CircleCheck
										aria-hidden
										className="m-auto text-black"
									/>
								);

								return (
									<td
										aria-label={ariaLabel}
										className="border border-grey-200 p-4 text-center text-sm font-normal text-grey-500"
										key={plan.name + index}
									>
										<span className="mx-auto block max-w-[263px]">
											{limitedValues}
										</span>
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default PlansFeatures;
